import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight } from 'lucide-react';
import SEO from './SEO';

/**
 * Shared layout for long-form SEO landing pages (curcumin-vs-turmeric,
 * bcm-95-explained, joint-health-supplements-uk).
 *
 * - Renders Article JSON-LD so Google indexes the page as an Article
 * - Injects per-page SEO (title, description, canonical)
 * - Standard breadcrumbs + hero + sticky table of contents on desktop
 * - MHRA/ASA-compliant by default — copy is written in the page itself
 *
 * Props:
 *   title          short page title for <title> tag (50-65 chars)
 *   description    meta description (120-160 chars)
 *   pathOverride   canonical path (e.g. "/curcumin-vs-turmeric")
 *   heading        on-page H1 (longer-form than <title>)
 *   subheading     hook sentence under the H1
 *   tag            small uppercase eyebrow tag (e.g. "Curcumin Guide")
 *   datePublished  ISO date string for the Article schema
 *   sections       [{ id, heading, body: ReactNode }]  rendered in order
 *   ctaProductId   route to push the reader towards
 */
export default function LandingArticle({
  title,
  description,
  keywords,
  pathOverride,
  heading,
  subheading,
  tag,
  datePublished,
  dateModified,
  sections,
  ctaProductId = '94056720-f372-4eef-bcfe-74063137a6e5',
}) {
  const modified = dateModified || datePublished;
  return (
    <div className="min-h-screen py-20 md:py-28 bg-gradient-to-b from-emerald-50/40 via-white to-white">
      <SEO title={title} description={description} keywords={keywords} pathOverride={pathOverride} />
      {/* Article + author (E-E-A-T) + BreadcrumbList JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Article',
                headline: heading,
                description,
                datePublished,
                dateModified: modified,
                author: { '@type': 'Organization', name: 'IIY Phytoventra', url: 'https://iiypv.com/' },
                contributor: { '@id': 'https://iiypv.com/#dr-benny-antony' },
                publisher: { '@id': 'https://iiypv.com/#organization' },
                mainEntityOfPage: { '@type': 'WebPage', '@id': `https://iiypv.com${pathOverride}` },
                image: 'https://iiypv.com/bcm95-bottle.jpg',
                inLanguage: 'en-GB',
              },
              {
                '@type': 'Person',
                '@id': 'https://iiypv.com/#dr-benny-antony',
                name: 'Dr Benny Antony',
                honorificSuffix: 'PhD',
                jobTitle: 'Curcumin researcher; co-developer of the patented BCM-95® (Curcugreen®) extract',
                affiliation: { '@type': 'Organization', name: 'Arjuna Natural', url: 'https://www.arjunanatural.com/' },
                knowsAbout: ['Curcumin', 'BCM-95®', 'Curcugreen', 'Turmeric bioavailability', 'Nutraceuticals'],
              },
              {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://iiypv.com/' },
                  { '@type': 'ListItem', position: 2, name: tag, item: `https://iiypv.com${pathOverride}` },
                ],
              },
            ],
          }),
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Breadcrumbs */}
        <nav className="text-xs text-muted-foreground mb-6 flex items-center gap-1.5" aria-label="Breadcrumb" data-testid="landing-breadcrumb">
          <Link to="/" className="hover:text-primary">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-foreground font-medium">{tag}</span>
        </nav>

        <div className="grid lg:grid-cols-[1fr_280px] gap-10 lg:gap-16">
          {/* Article body */}
          <article>
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-bold bg-emerald-100 text-emerald-800 mb-4">
                {tag}
              </span>
              <h1 className="text-3xl md:text-5xl font-bold font-heading text-emerald-900 leading-tight mb-3" data-testid="landing-h1">
                {heading}
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl">
                {subheading}
              </p>

              {/* Author / E-E-A-T byline */}
              <div className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-slate-500" data-testid="article-byline">
                <span>By the <strong className="text-slate-700 font-semibold">IIY Phytoventra editorial team</strong></span>
                <span className="text-slate-300">·</span>
                <span>Scientific advisor: <strong className="text-slate-700 font-semibold">Dr Benny Antony, PhD</strong> (co-developer of the patented BCM-95<sup>&reg;</sup> extract)</span>
                <span className="text-slate-300">·</span>
                <span>Updated {new Date(modified).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
              </div>
            </motion.div>

            <hr className="my-10 border-emerald-700/10" />

            {/* Sections */}
            <div className="space-y-12">
              {sections.map((s) => (
                <section key={s.id} id={s.id} data-testid={`section-${s.id}`}>
                  <h2 className="text-2xl md:text-3xl font-bold font-heading text-emerald-900 mb-4 scroll-mt-32">
                    {s.heading}
                  </h2>
                  <div className="prose prose-slate max-w-none text-[15px] md:text-base leading-relaxed text-slate-700 space-y-4">
                    {s.body}
                  </div>
                </section>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-16 rounded-3xl bg-gradient-to-br from-emerald-700 to-emerald-900 text-white p-8 md:p-10 shadow-xl">
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-200 mb-2">Ready to try?</p>
              <h3 className="text-2xl md:text-3xl font-bold font-heading mb-3 leading-tight">
                BCM-95® &mdash; the curcumin formulation behind this article.
              </h3>
              <p className="text-emerald-50/90 mb-5 max-w-2xl">
                60 vegetarian capsules. 1000 mg Purified Turmeric Extract Curcugreen<sup>&reg;</sup> per 2 capsules (of which 650 mg curcumin). All-inclusive of UK VAT &amp; FREE Royal Mail 48 delivery. First-time buyer welcome offer: <strong className="text-white">£37.99</strong>.
              </p>
              <Link
                to={`/products/${ctaProductId}`}
                data-testid="landing-cta"
                className="inline-flex items-center gap-2 bg-white text-emerald-900 font-bold py-3 px-6 rounded-full hover:bg-emerald-50 transition-colors shadow-lg hover:shadow-xl"
              >
                Shop BCM-95® <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <p className="text-[11px] text-muted-foreground italic mt-8 leading-relaxed max-w-3xl">
              Food supplements should not be used as a substitute for a varied and balanced diet and a healthy lifestyle. Keep out of reach of children. Not intended to diagnose, treat, cure or prevent any disease. If you are pregnant, breastfeeding, taking medication or under medical supervision, consult a healthcare professional before use.
            </p>
          </article>

          {/* Sticky ToC (desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700 mb-3">On this page</div>
              <ul className="space-y-2 text-sm border-l-2 border-emerald-700/15 pl-4">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="text-slate-600 hover:text-emerald-700 transition-colors block leading-snug">
                      {s.heading}
                    </a>
                  </li>
                ))}
              </ul>
              <Link
                to={`/products/${ctaProductId}`}
                className="mt-6 inline-flex items-center gap-1.5 text-emerald-700 font-bold text-sm hover:gap-2.5 transition-all"
              >
                Shop now <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
